// Music/SFX bed track — slim per-asset stack of up to 2 rows that lives
// above the Timeline. Lets the talent layer two independent audio beds
// per asset (e.g. music + ambience) for previewing voice-over-music or
// compensating a muted video. The beds:
//   - are uploaded via per-row hidden <input type="file"> restricted to
//     MP3 + WAV via the `accept` attribute. We deliberately don't accept
//     `audio/*` — keeping it tight matches the format of files the talent
//     actually layers (loops, ambience clips, music beds) and avoids
//     surprises with niche containers we don't decode robustly.
//   - live in-memory only (not persisted) — re-upload after reload
//   - placement (`offset`/`sourceOffset`) lebt ebenfalls nur runtime auf dem Bed
//   - play alongside voice during Timeline-preview, mixed through their
//     own GainNodes in useTimelinePreview / useSTSMasterPreview (auto-
//     duck when voice plays — same edge transitions as the <video> bus)
//   - can be removed / replaced independently per slot
//
// Layout / slot semantics:
//   - Slot 0 (Track 1) is always the primary slot
//   - Slot 1 (Track 2) only shows when slot 0 is filled — a subtle
//     "+ Add Track 2" CTA appears below the populated track 1
//   - Removing slot 0 promotes slot 1 → slot 0 (session store enforces)
//   - Max 2 tracks total
//
// Populated state renders the bed's waveform full-width across the row.
// Title pill always visible centered; Replace + X live in a hover-only
// overlay. Same chip language as the Timeline chips.

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useSessionStore, type SfxBed } from '@/stores/session';
import { useVideoStore } from '@/stores/video';
import { getAssetTimelineDuration } from '@/lib/timeline';
import { resolveBedPlacement } from '@/lib/bedPlacement';
import { getWaveformSvg } from '@/lib/waveformSvg';
import styles from './SfxTrack.module.css';

function formatMmSs(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0:00';
  const mm = Math.floor(seconds / 60);
  const ss = Math.floor(seconds - mm * 60);
  return `${mm}:${ss.toString().padStart(2, '0')}`;
}

/** Decode the file's duration without spinning up a full Web Audio context.
 *  An off-DOM <audio> + loadedmetadata is enough and is consistent with how
 *  voice takes get their duration in TTSView. */
function probeAudioDuration(url: string): Promise<number> {
  return new Promise((resolve) => {
    const probe = new Audio();
    probe.preload = 'metadata';
    const cleanup = () => {
      probe.removeEventListener('loadedmetadata', onMeta);
      probe.removeEventListener('error', onErr);
    };
    const onMeta = () => {
      const d = probe.duration;
      cleanup();
      resolve(Number.isFinite(d) && d > 0 ? d : 0);
    };
    const onErr = () => {
      cleanup();
      resolve(0);
    };
    probe.addEventListener('loadedmetadata', onMeta);
    probe.addEventListener('error', onErr);
    probe.src = url;
  });
}

/** Build an `SfxBed` from a chosen File. */
async function buildBedFromFile(file: File): Promise<SfxBed> {
  const buffer = await file.arrayBuffer();
  const blob = new Blob([buffer], { type: file.type || 'audio/mpeg' });
  const url = URL.createObjectURL(blob);
  const duration = await probeAudioDuration(url);
  return {
    id: `bed-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: file.name,
    duration,
    url,
    buffer,
    mimeType: file.type || 'audio/mpeg',
  };
}

/** Auto-set customDuration from the first-loaded bed when it makes sense
 *  (custom asset, no admin targetDuration, no user-entered length). */
function maybeAutoSetCustomDuration(currentAssetIdx: number, duration: number) {
  if (duration <= 0) return;
  const state = useSessionStore.getState();
  const merged = [
    ...(state.config?.assets ?? []),
    ...state.customAssets.map((ca) => ({
      ...ca,
      assetType: 'free' as const,
      _isCustom: true as const,
    })),
  ];
  const asset = merged[currentAssetIdx];
  const current = state.customDurations[currentAssetIdx];
  if (asset?._isCustom && !asset.targetDuration && !current) {
    state.setCustomDuration(currentAssetIdx, Math.ceil(duration * 10) / 10);
  }
}

// ─── Per-row child component ──────────────────────────────────────────

interface RowProps {
  bed: SfxBed | null;
  /** True when no bed is loaded AND this slot is allowed to receive one
   *  (slot 0 always allowed; slot 1 only when slot 0 is filled). */
  canAdd: boolean;
  /** Slot 1 renders the empty-state CTA in a slimmer style so the
   *  hierarchy primary-track-on-top reads visually. */
  secondary: boolean;
  /** Only-existing-bed contributes the auto-set custom-duration on first
   *  load; the second bed never auto-sets so the user's chosen length
   *  isn't surprise-overridden by a longer ambience layer. */
  isFirstSlot: boolean;
  /** Asset timeline length in seconds — drives clip geometry + drag range. */
  assetLen: number;
  /** Persist a placement update (offset or sourceOffset) to the session store. */
  onPlacementChange: (patch: { offset?: number; sourceOffset?: number }) => void;
  onAdd: (file: File) => void;
  onReplace: (file: File) => void;
  onRemove: () => void;
}

function SfxTrackRow({
  bed,
  canAdd,
  secondary,
  isFirstSlot,
  assetLen,
  onPlacementChange,
  onAdd,
  onReplace,
  onRemove,
}: RowProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const inputId = useId();
  // Lane ref — measures live pixel width to convert pointer delta → seconds.
  const laneRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);
  // Refs to the active drag listeners so the unmount cleanup can remove them
  // if the row unmounts mid-drag (otherwise they persist with a stale closure).
  const activeMoveRef = useRef<((e: PointerEvent) => void) | null>(null);
  const activeUpRef = useRef<((e: PointerEvent) => void) | null>(null);

  // Unmount cleanup — remove any live pointer listeners from window.
  useEffect(() => {
    return () => {
      if (activeMoveRef.current) {
        window.removeEventListener('pointermove', activeMoveRef.current);
        activeMoveRef.current = null;
      }
      if (activeUpRef.current) {
        window.removeEventListener('pointerup', activeUpRef.current);
        activeUpRef.current = null;
      }
    };
  }, []);

  const onInputChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const file = ev.target.files?.[0];
      if (file) {
        if (bed) onReplace(file);
        else onAdd(file);
      }
      // Reset so re-selecting the same filename re-fires change.
      ev.target.value = '';
    },
    [bed, onAdd, onReplace],
  );

  const onPickClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const inputEl = useMemo(
    () => (
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept="audio/mpeg,audio/wav,audio/x-wav,.mp3,.wav"
        className={styles.hiddenInput}
        onChange={onInputChange}
      />
    ),
    [inputId, onInputChange],
  );

  // Waveform SVG (populated only). Cached per buffer ref via getWaveformSvg.
  const [wfUrl, setWfUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!bed?.buffer) {
      setWfUrl(null);
      return;
    }
    let cancelled = false;
    void getWaveformSvg(bed.buffer).then((url) => {
      if (!cancelled) setWfUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [bed?.buffer]);

  // Placement resolution — null when no bed loaded.
  const placement = bed ? resolveBedPlacement(bed, assetLen) : null;

  // Pointer drag handler. setPointerCapture keeps the drag even if the
  // pointer leaves the element (same pattern as Timeline chips).
  const onClipPointerDown = useCallback(
    (ev: React.PointerEvent<HTMLDivElement>) => {
      if (!bed || !placement) return;
      const lane = laneRef.current;
      if (!lane) return;
      const laneW = lane.getBoundingClientRect().width;
      if (laneW <= 0 || assetLen <= 0) return;
      const pxPerSec = laneW / assetLen;
      const bedLen = bed.duration || 0;
      // If drag range is effectively zero, do nothing (bed == asset length).
      if (placement.mode === 'place' && assetLen - bedLen < 0.05) return;
      if (placement.mode === 'window' && bedLen - assetLen < 0.05) return;

      ev.currentTarget.setPointerCapture(ev.pointerId);
      setDragging(true);
      const startX = ev.clientX;
      const startOffset =
        placement.mode === 'place'
          ? placement.timelineStart
          : placement.sourceStart;

      const onMove = (e: PointerEvent) => {
        const dxSec = (e.clientX - startX) / pxPerSec;
        if (placement.mode === 'place') {
          // Drag right → clip later on the timeline.
          const next = Math.max(0, Math.min(assetLen - bedLen, startOffset + dxSec));
          onPlacementChange({ offset: Math.round(next * 10) / 10 });
        } else {
          // Drag right → reveal earlier source (window moves left).
          const next = Math.max(0, Math.min(bedLen - assetLen, startOffset - dxSec));
          onPlacementChange({ sourceOffset: Math.round(next * 10) / 10 });
        }
      };
      const onUp = (e: PointerEvent) => {
        try {
          (ev.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
        } catch {
          /* ignore — currentTarget may be null after React handler returns */
        }
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        activeMoveRef.current = null;
        activeUpRef.current = null;
        setDragging(false);
      };
      activeMoveRef.current = onMove;
      activeUpRef.current = onUp;
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [bed, placement, assetLen, onPlacementChange],
  );

  if (!bed) {
    if (!canAdd) return null;
    const cls = secondary
      ? `${styles.addBtn} ${styles.addBtnSecondary}`
      : styles.addBtn;
    const label = secondary ? 'Add Track 2' : 'Add Audio (Music/SFX)';
    return (
      <div className={styles.trackRow}>
        {inputEl}
        <button
          type="button"
          className={cls}
          onClick={onPickClick}
          title={
            isFirstSlot
              ? 'Add a music or SFX bed for this asset (preview only)'
              : 'Add a second music or SFX bed (max 2)'
          }
        >
          <i className="ph-bold ph-music-notes-plus" />
          <span>{label}</span>
        </button>
      </div>
    );
  }

  // Clip geometry as CSS percentages of the lane (pxPerSec cancels out).
  const bedLen = bed.duration || 0;
  const clipWidthPct = assetLen > 0 ? (bedLen / assetLen) * 100 : 100;
  const clipLeftPct =
    placement?.mode === 'place' && assetLen > 0
      ? (placement.timelineStart / assetLen) * 100
      : 0;
  const clipTranslatePct =
    placement?.mode === 'window' && bedLen > 0
      ? -(placement.sourceStart / bedLen) * 100
      : 0;
  // Fade overlays appear on drag in window-mode to show which direction has
  // more source audio.
  const showFadeLeft =
    dragging && placement?.mode === 'window' && placement.sourceStart > 0.05;
  const showFadeRight =
    dragging &&
    placement?.mode === 'window' &&
    placement.sourceStart < bedLen - assetLen - 0.05;

  // Compute whether drag is allowed — bed drag range must be at least 0.05s.
  const canDrag = placement
    ? placement.mode === 'place'
      ? assetLen - bedLen >= 0.05
      : bedLen - assetLen >= 0.05
    : false;

  return (
    <div className={styles.trackRow}>
      {inputEl}
      <div ref={laneRef} className={styles.lane} title={bed.name}>
        <div
          className={`${styles.clip} ${dragging ? styles.clipDragging : ''} ${!canDrag ? styles.clipNoDrag : ''}`}
          style={{
            left: `${clipLeftPct}%`,
            width: `${clipWidthPct}%`,
            transform: `translateX(${clipTranslatePct}%)`,
            backgroundImage: wfUrl ? `url("${wfUrl}")` : undefined,
            backgroundSize: wfUrl ? '100% 100%' : undefined,
            backgroundRepeat: wfUrl ? 'no-repeat' : undefined,
          }}
          onPointerDown={onClipPointerDown}
        />
        {showFadeLeft && <div className={styles.fadeLeft} />}
        {showFadeRight && <div className={styles.fadeRight} />}
        <span
          className={`${styles.titlePill} ${dragging ? styles.titlePillHidden : ''}`}
        >
          {bed.name}
        </span>
        <div className={styles.rowHoverOverlay}>
          <span className={styles.rowDuration}>{formatMmSs(bed.duration)}</span>
          <button
            type="button"
            className={styles.replaceBtn}
            onClick={onPickClick}
            title="Replace bed audio"
          >
            Replace
          </button>
          <button
            type="button"
            className={styles.removeBtn}
            onClick={onRemove}
            title="Remove bed audio"
            aria-label="Remove bed audio"
          >
            <i className="ph-bold ph-x" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Container ────────────────────────────────────────────────────────

export function SfxTrack() {
  const currentAssetIdx = useSessionStore((s) => s.currentAssetIdx);
  const sfxBeds = useSessionStore((s) => s.sfxBeds);
  const addSfxBed = useSessionStore((s) => s.addSfxBed);
  const replaceSfxBed = useSessionStore((s) => s.replaceSfxBed);
  const removeSfxBed = useSessionStore((s) => s.removeSfxBed);
  const setBedPlacement = useSessionStore((s) => s.setBedPlacement);
  const videoDurations = useVideoStore((s) => s.durations);
  const customDurations = useSessionStore((s) => s.customDurations);
  const config = useSessionStore((s) => s.config);
  const customAssets = useSessionStore((s) => s.customAssets);

  const bedsSlot = sfxBeds[currentAssetIdx];
  const beds = useMemo(() => bedsSlot ?? [], [bedsSlot]);
  const slot0 = beds[0] ?? null;
  const slot1 = beds[1] ?? null;
  // Slot 1's "Add Track 2" CTA only shows once slot 0 is populated.
  const slot1CanAdd = slot0 !== null;

  // Asset timeline length — used to compute clip geometry and drag range for
  // both bed rows. Mirrors the duration source used by TTSView's ruler.
  const assetLen = useMemo(() => {
    const merged = [
      ...(config?.assets ?? []),
      ...customAssets.map((ca) => ({ ...ca, assetType: 'free' as const, _isCustom: true as const })),
    ];
    const asset = merged[currentAssetIdx];
    const longestBed =
      beds.length > 0 ? beds.reduce((m, b) => Math.max(m, b.duration || 0), 0) : null;
    return getAssetTimelineDuration(
      currentAssetIdx,
      videoDurations,
      asset?.targetDuration ?? null,
      customDurations,
      longestBed,
    );
  }, [config, customAssets, currentAssetIdx, videoDurations, customDurations, beds]);

  const handleAdd = useCallback(
    async (file: File) => {
      const bed = await buildBedFromFile(file);
      addSfxBed(currentAssetIdx, bed);
      maybeAutoSetCustomDuration(currentAssetIdx, bed.duration);
    },
    [currentAssetIdx, addSfxBed],
  );

  const handleReplace = useCallback(
    async (trackIdx: 0 | 1, file: File) => {
      const existing =
        useSessionStore.getState().sfxBeds[currentAssetIdx]?.[trackIdx];
      if (existing?.url) {
        try { URL.revokeObjectURL(existing.url); } catch { /* ignore */ }
      }
      const bed = await buildBedFromFile(file);
      replaceSfxBed(currentAssetIdx, trackIdx, bed);
    },
    [currentAssetIdx, replaceSfxBed],
  );

  const handleRemove = useCallback(
    (trackIdx: 0 | 1) => {
      const existing =
        useSessionStore.getState().sfxBeds[currentAssetIdx]?.[trackIdx];
      if (existing?.url) {
        try { URL.revokeObjectURL(existing.url); } catch { /* ignore */ }
      }
      removeSfxBed(currentAssetIdx, trackIdx);
    },
    [currentAssetIdx, removeSfxBed],
  );

  return (
    <div className={styles.root}>
      <SfxTrackRow
        bed={slot0}
        assetLen={assetLen}
        canAdd={true}
        secondary={false}
        isFirstSlot={true}
        onAdd={(file) => void handleAdd(file)}
        onReplace={(file) => void handleReplace(0, file)}
        onRemove={() => handleRemove(0)}
        onPlacementChange={(patch) => setBedPlacement(currentAssetIdx, 0, patch)}
      />
      <SfxTrackRow
        bed={slot1}
        assetLen={assetLen}
        canAdd={slot1CanAdd}
        secondary={true}
        isFirstSlot={false}
        onAdd={(file) => void handleAdd(file)}
        onReplace={(file) => void handleReplace(1, file)}
        onRemove={() => handleRemove(1)}
        onPlacementChange={(patch) => setBedPlacement(currentAssetIdx, 1, patch)}
      />
    </div>
  );
}
